/**
 * THE SHOPS THAT RUN ON ONE SCREEN OF CALLS AND NOTHING ELSE.
 *
 * JEV for New York Intelligence Agency, 2026-10-15 (D20): "Neelum wants the
 * system stripped down to only: phone number, name, phone recording, phone
 * transcript, and SEND AGREEMENT button. Remove all car-related language
 * and extra features. Also check Laura's permissions - she doesn't have
 * the send agreement button."
 *
 * A list of account ids rather than a desk_mode, because desk_mode has a
 * check constraint and widening it is a migration; this is one shop asking
 * for a starting point, not a new kind of desk yet. When a second shop
 * wants it, or this one grows, it belongs in the column.
 *
 * Pure and in src/domain because the browser (which bar to draw) and the
 * server (who may send an agreement) both ask, and two lists drift.
 */

export const CALL_DESK_ACCOUNTS: ReadonlySet<string> = new Set([
  "d60137e5-8b06-4eb7-a1c5-066255786bde", // New York Intelligence Agency
]);

export const isCallDesk = (accountId: string | null | undefined): boolean =>
  !!accountId && CALL_DESK_ACCOUNTS.has(accountId);

/**
 * Who may send an agreement for signature.
 *
 * Everywhere else a contract goes out in the shop's name from the desk
 * roles only. On a call desk the person who took the call sends it: Laura
 * had no button because her login is not one of those three roles, and the
 * screen she was asked to work from has nothing else on it. Countersigning
 * is NOT widened; that stays with the owner or the named countersigners.
 */
export const maySendAgreement = (role: string, accountId: string | null | undefined): boolean =>
  isCallDesk(accountId) || ["admin", "supervisor", "advisor"].includes(role);

/**
 * WHAT A CLIENT READS ABOVE THE APPROVE BUTTON, in the firm's own words.
 *
 * Laura Dooley at NYIA, 2026-10-16: replace "Happy with this?" with their
 * next steps. Keyed by account, not trade, because it names their Zelle
 * address and that must never print on another shop's page.
 */
const APPROVAL_STEPS: Readonly<Record<string, string>> = {
  "d60137e5-8b06-4eb7-a1c5-066255786bde":
    "Next Steps: Approve this quote and we'll send you our Client Retainer Agreement. "
    + "Payment is due prior to work starting on your case. Payment can be remitted via Zelle "
    + "to Global.nyia@gmail.com or call our office to utilize other payment methods. Thank you.",
};

export const approvalStepsFor = (accountId: string | null | undefined): string | null =>
  (accountId && APPROVAL_STEPS[accountId]) || null;

/**
 * SEND TO NK: the stickier callers go to the owner.
 *
 * Neelum at NYIA, 2026-10-21, asking how Laura passes her the hard calls:
 * "Maybe a button under the spam button that says 'send to NK'?" A cell
 * phone cannot hand a call over, so the button texts the owner who called
 * and their number, from the firm's line, and she rings them herself.
 * Keyed by account because it names one person's cell.
 */
export type Handoff = { label: string; name: string; phone10: string };
const HANDOFFS: Readonly<Record<string, Handoff>> = {
  "d60137e5-8b06-4eb7-a1c5-066255786bde": { label: "Send to NK", name: "Neelum", phone10: "6177100222" },
};

export const handoffFor = (accountId: string | null | undefined): Handoff | null =>
  (accountId && HANDOFFS[accountId]) || null;

/* The text she gets. The caller's number is spelled out so it taps to call. */
/* `number` is how to write a caller abroad ("+39 3508512278"); +1 is spelled from phone10. */
export function handoffText(o: { to: string; caller: string; phone10: string; note: string; from: string; number?: string }): string {
  const p = o.phone10;
  const num = o.number ?? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;
  const lines = [
    `Hi ${o.to}, please call ${o.caller.trim() || "this caller"} at ${num}.`,
    ...(o.note.trim() ? [`Notes: ${o.note.trim()}`] : []),
    `Sent from the desk${o.from.trim() ? ` by ${o.from.trim()}` : ""}.`,
  ];
  return lines.join("\n").slice(0, 1200);
}

/**
 * THE DATES DOWN THE SIDE OF CALLS (Neelum, 2026-10-29: "a scroll bar
 * function in calls that allows us to scroll back to a specific date ...
 * scroll bar on side with dates", with a phone's A to Z index as the
 * picture). The list is newest first and read forty at a time, so a date
 * is a place in the list: for each day, where its first caller sits.
 * Days are the firm's own, New York time, not the server's.
 */
export type DayMark = { d: string; i: number };
export function dayMarks(lasts: readonly string[], tz = "America/New_York"): DayMark[] {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
  const out: DayMark[] = [];
  lasts.forEach((at, i) => {
    const d = day.format(new Date(at));
    if (out[out.length - 1]?.d !== d) out.push({ d, i });
  });
  return out;
}
